/**
 * Notification Settings Component
 * Manages email and SMS notification preferences
 */
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { Bell, Mail, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';

export default function NotificationSettings() {
  const { user } = useAuthStore();
  const { updateSettings } = useSettingsStore();

  const [emailNotifications, setEmailNotifications] = useState(user?.email_notifications ?? true);
  const [weeklyDigest, setWeeklyDigest] = useState(user?.weekly_digest ?? true);
  const [smsOptIn, setSmsOptIn] = useState(user?.sms_opt_in ?? false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Sync with user data when it changes
  useEffect(() => {
    if (user) {
      setEmailNotifications(user.email_notifications ?? true);
      setWeeklyDigest(user.weekly_digest ?? true);
      setSmsOptIn(user.sms_opt_in ?? false);
    }
  }, [user]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await updateSettings({
        email_notifications: emailNotifications,
        weekly_digest: weeklyDigest,
        sms_opt_in: smsOptIn,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save notification preferences');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if anything has changed
  const hasChanges =
    emailNotifications !== (user?.email_notifications ?? true) ||
    weeklyDigest !== (user?.weekly_digest ?? true) ||
    smsOptIn !== (user?.sms_opt_in ?? false);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-dark-text mb-2">Notifications</h2>
        <p className="text-dark-text-muted text-sm">
          Manage how and when PARACLETE contacts you.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="p-4 bg-green-900/30 border border-green-500/50 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-green-400 text-sm">Notification preferences saved successfully!</p>
        </div>
      )}

      {/* Email Notifications Section */}
      <div className="bg-dark-bg/30 rounded-lg p-4 border border-dark-border/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-accent-blue/10 rounded-lg">
            <Mail className="w-5 h-5 text-accent-blue" />
          </div>
          <div>
            <h3 className="font-medium text-dark-text">Email Notifications</h3>
            <p className="text-sm text-dark-text-muted">Receive updates via email</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Email Notifications Toggle */}
          <label className="flex items-center justify-between cursor-pointer group">
            <div>
              <span className="text-dark-text font-medium">Email notifications</span>
              <p className="text-sm text-dark-text-muted mt-0.5">
                Receive important account alerts and updates
              </p>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-dark-border rounded-full peer peer-checked:bg-accent-blue transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
            </div>
          </label>

          {/* Weekly Digest Toggle */}
          <label className="flex items-center justify-between cursor-pointer group">
            <div>
              <span className="text-dark-text font-medium">Weekly digest</span>
              <p className="text-sm text-dark-text-muted mt-0.5">
                Receive a weekly summary of your ministry insights
              </p>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={weeklyDigest}
                onChange={(e) => setWeeklyDigest(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-dark-border rounded-full peer peer-checked:bg-accent-blue transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
            </div>
          </label>
        </div>
      </div>

      {/* SMS Notifications Section */}
      <div className="bg-dark-bg/30 rounded-lg p-4 border border-dark-border/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-500/10 rounded-lg">
            <MessageSquare className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h3 className="font-medium text-dark-text">SMS Notifications</h3>
            <p className="text-sm text-dark-text-muted">Receive text message alerts</p>
          </div>
        </div>

        <label className="flex items-start gap-4 cursor-pointer group">
          <div className="relative mt-0.5">
            <input
              type="checkbox"
              checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-dark-border rounded-full peer peer-checked:bg-green-500 transition-colors" />
            <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
          </div>
          <div className="flex-1">
            <span className="text-dark-text font-medium">SMS notifications</span>
            <p className="text-sm text-dark-text-muted mt-1">
              I agree to receive transactional SMS notifications including verification codes,
              appointment reminders, and account alerts from PARACLETE. Message & data rates may apply.
              Reply STOP to cancel.
            </p>
            <p className="text-xs text-dark-text-muted mt-2">
              View our{' '}
              <a href="https://ministry.builders/privacy" className="text-accent-blue hover:underline" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://ministry.builders/terms" className="text-accent-blue hover:underline" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>.
            </p>
            {user?.sms_consent_date && smsOptIn && (
              <p className="text-xs text-dark-text-muted mt-2">
                Opted in on: {new Date(user.sms_consent_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </label>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={`
            px-6 py-2.5 rounded-lg font-medium transition-all duration-200
            ${hasChanges
              ? 'bg-accent-blue text-white hover:bg-accent-blue/90'
              : 'bg-dark-border text-dark-text-muted cursor-not-allowed'
            }
            ${isSaving ? 'opacity-75 cursor-wait' : ''}
          `}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
